import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiBook, FiTarget, FiAward } = FiIcons;

const NonfictionForm = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: '',
    problem: '',
    solution: '',
    transformation: '',
    targetAudience: '',
    approach: '',
    credentials: '',
    keyPoints: '',
    biblicalFoundation: '',
    practicalApplication: '',
    personalStory: '',
    premise: '',
    outcome: ''
  });

  const [currentSection, setCurrentSection] = useState(0);

  const sections = [
    {
      title: 'Book Basics',
      icon: FiBook,
      fields: ['title', 'author', 'category', 'targetAudience']
    },
    {
      title: 'Problem & Solution',
      icon: FiTarget,
      fields: ['problem', 'solution', 'transformation', 'outcome']
    },
    {
      title: 'Authority & Content',
      icon: FiAward,
      fields: ['credentials', 'approach', 'keyPoints', 'biblicalFoundation', 'practicalApplication', 'personalStory', 'premise']
    }
  ];

  const formFields = {
    title: {
      label: 'Book Title',
      placeholder: 'Enter your book title',
      type: 'text',
      required: true
    },
    author: {
      label: 'Author Name',
      placeholder: 'Your name',
      type: 'text',
      required: true
    },
    category: {
      label: 'Book Category',
      type: 'select',
      required: true,
      options: [
        { value: '', label: 'Select category...' },
        { value: 'devotional', label: 'Devotional' },
        { value: 'study', label: 'Bible Study' },
        { value: 'self-help', label: 'Christian Living/Self-Help' },
        { value: 'memoir', label: 'Memoir/Testimony' },
        { value: 'parenting', label: 'Christian Parenting' },
        { value: 'marriage', label: 'Marriage & Relationships' },
        { value: 'leadership', label: 'Christian Leadership' },
        { value: 'theology', label: 'Theology/Doctrine' },
        { value: 'evangelism', label: 'Evangelism/Missions' },
        { value: 'prayer', label: 'Prayer & Spiritual Disciplines' },
        { value: 'healing', label: 'Healing & Recovery' }
      ]
    },
    problem: {
      label: 'What Problem Does Your Book Solve?',
      placeholder: 'e.g., struggling with anxiety, feeling disconnected from God, lack of purpose',
      type: 'textarea',
      required: true,
      hint: 'What pain point or challenge does your reader face?'
    },
    solution: {
      label: 'What Solution Do You Offer?',
      placeholder: 'e.g., biblical principles for peace, practical steps to intimacy with God',
      type: 'textarea',
      required: true,
      hint: 'How does your book help solve their problem?'
    },
    transformation: {
      label: 'What Transformation Will Readers Experience?',
      placeholder: 'e.g., find lasting peace, discover their God-given purpose, strengthen their marriage',
      type: 'textarea',
      required: true,
      hint: 'What will be different in their life after reading your book?'
    },
    targetAudience: {
      label: 'Target Audience',
      placeholder: 'e.g., Christian women, new believers, church leaders, struggling marriages',
      type: 'text',
      required: true,
      hint: 'Who specifically is this book written for?'
    },
    approach: {
      label: 'Your Approach/Method',
      placeholder: 'e.g., combines biblical wisdom with practical exercises, shares personal journey of healing',
      type: 'textarea',
      hint: 'What makes your approach unique? How do you help readers?'
    },
    credentials: {
      label: 'Author Credentials/Authority',
      placeholder: 'e.g., pastor for 20 years, licensed counselor, overcame addiction',
      type: 'text',
      hint: 'What qualifies you to write on this topic?'
    },
    keyPoints: {
      label: 'Key Points/Benefits',
      placeholder: 'practical prayer strategies, 5 steps to forgiveness, biblical view of self-worth',
      type: 'textarea',
      hint: 'List the main points or benefits (separate with commas)'
    },
    biblicalFoundation: {
      label: 'Biblical Foundation',
      placeholder: 'Describe how your book is grounded in Scripture',
      type: 'textarea',
      hint: 'How does your book connect to biblical truth?'
    },
    practicalApplication: {
      label: 'Practical Application',
      placeholder: 'How will readers apply what they learn?',
      type: 'textarea',
      hint: 'What practical tools, exercises, or actions will readers take?'
    },
    personalStory: {
      label: 'Personal Story Element',
      placeholder: 'Briefly describe any personal story or testimony you share',
      type: 'textarea',
      hint: 'Do you share personal experiences that relate to the topic?'
    },
    premise: {
      label: 'Book Premise',
      placeholder: 'Write 2-3 sentences describing what your book is about...',
      type: 'textarea',
      required: true,
      hint: 'Summarize your book\'s main message and approach'
    },
    outcome: {
      label: 'Expected Outcome',
      placeholder: 'e.g., experience God\'s peace, build stronger relationships, find their calling',
      type: 'text',
      hint: 'What will readers be able to do or experience after reading?'
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const isCurrentSectionValid = () => {
    const currentFields = sections[currentSection].fields;
    return currentFields.every(field => {
      const fieldConfig = formFields[field];
      if (fieldConfig.required) {
        return formData[field] && formData[field].trim() !== '';
      }
      return true;
    });
  };

  const renderField = (fieldName) => {
    const field = formFields[fieldName];
    const value = formData[fieldName];

    return (
      <motion.div
        key={fieldName}
        className="space-y-2"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <label className="block text-sm font-semibold text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {field.type === 'select' ? (
          <select
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            required={field.required}
          >
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none"
            required={field.required}
          />
        ) : (
          <input
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
            required={field.required}
          />
        )}
        
        {field.hint && (
          <p className="text-sm text-gray-500 italic">{field.hint}</p>
        )}
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bg-gradient-to-r from-green-500 to-blue-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={sections[currentSection].icon} className="text-2xl" />
              <div>
                <h2 className="text-2xl font-bold">Nonfiction Book Description</h2>
                <p className="text-green-100">Step {currentSection + 1} of {sections.length}: {sections[currentSection].title}</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6">
            {sections[currentSection].fields.map(fieldName => renderField(fieldName))}
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-2">
              {sections.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index === currentSection 
                      ? 'bg-green-600' 
                      : index < currentSection 
                        ? 'bg-blue-500' 
                        : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex space-x-4">
              {currentSection > 0 && (
                <button
                  type="button"
                  onClick={prevSection}
                  className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} />
                  <span>Previous</span>
                </button>
              )}

              {currentSection < sections.length - 1 ? (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={!isCurrentSectionValid()}
                  className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!isCurrentSectionValid()}
                  className="flex items-center space-x-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Generate Description</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              )}
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default NonfictionForm;